import React, { FC, memo, useState, useMemo, useLayoutEffect } from 'react'

import { useSetFocus } from '@please/lrud'
import { useNavigate } from 'react-router-dom'

import { useAuth } from '@sportworld/js-sdk/src/context/authContext'

import { IconButton } from '@/components/IconButton'
import { MenuItem } from '@/components/MainSidebar/MenuItem'
import { Direction, focusIds, orientation } from '@/constants/focus'
import { icons } from '@/constants/icons'
import { settingsMenuItems } from '@/constants/menu'
import { paths } from '@/constants/paths'
import { useMenuItem } from '@/hooks/useMenuItem'
import { useCommonTranslation } from '@/i18n/useCommonTranslation'
import useStore from '@/store/useStore'
import { MenuItemType } from '@/types/menuTypes'
import { getMainHomePath } from '@/utils/route'
import { isDisabledMenuItem } from '@/utils/utils'

import {
  SettingsMenu,
  SettingsSidebarElement,
  MenuTitle,
  MenuButtonBack,
  ItemListWrapper,
  MenuItemWrapper,
  AppVersion,
  MenuWrapper,
} from './styles'

const SettingsSidebarRaw: FC = () => {
  const [sidebarFocused, setSidebarFocused] = useState(true)
  const [defaultFocusChild, setDefaultFocusChild] = useState(0)
  const setFocus = useSetFocus()
  const navigate = useNavigate()
  const { t } = useCommonTranslation()
  const appConfig = useStore((state) => state.common.appConfig)
  const appSettings = useStore((state) => state.common.appSettings)

  const { isAuthenticatedUserType } = useAuth()

  const { checkIsActiveMenuItem, selectMenuItem } = useMenuItem()

  const pathname = window.location.pathname.split('/').pop()

  const isSidebarHidden =
    pathname === paths.LOGIN ||
    pathname === paths.INDEX ||
    pathname === paths.ROOT

  const settingsList = useMemo(
    () =>
      settingsMenuItems.filter(({ hidden, name, visible }) => {
        const isDisabledItem = isDisabledMenuItem(appConfig, name)
        const isVisibleItem = !visible || visible === isAuthenticatedUserType
        return !hidden && !isDisabledItem && isVisibleItem
      }),
    [settingsMenuItems, appConfig, isAuthenticatedUserType],
  )

  useLayoutEffect(() => {
    if (sidebarFocused && pathname) {
      setFocus(pathname)
    }
  }, [sidebarFocused, pathname])

  const onMenuFocused = () => setSidebarFocused(true)
  const onMenuBlurred = () => setSidebarFocused(false)

  const backToMainMenu = () => {
    navigate(getMainHomePath())
  }

  const onMove = (e: MoveEvent) => {
    const currentChildIndex =
      e.direction === Direction.Forward
        ? Number(e.prevChildIndex) + 1
        : Number(e.prevChildIndex) - 1
    setDefaultFocusChild(currentChildIndex)
  }

  return (
    <SettingsSidebarElement
      focusId={focusIds.SIDEBAR}
      $isOpen={true}
      $isVisible={!isSidebarHidden}
      disabled={isSidebarHidden}
      elementType="aside"
      $isBlurUsed={!appSettings.isDisabledBlur}
    >
      <SettingsMenu
        elementType="nav"
        focusId={focusIds.SETTINGS_MENU}
        orientation={orientation.VERTICAL}
        onFocused={onMenuFocused}
        onBlurred={onMenuBlurred}
        defaultFocusChild={defaultFocusChild}
        onMove={onMove}
      >
        <MenuButtonBack>
          <IconButton
            iconSrc={icons.navigation.backArrow}
            onClick={backToMainMenu}
          />
        </MenuButtonBack>
        <MenuWrapper>
          <MenuTitle>{t('SETTINGS')}</MenuTitle>
          <ItemListWrapper>
            {settingsList.map((menuItem: MenuItemType) => {
              return (
                <MenuItemWrapper
                  key={menuItem.id}
                  $hasDivider={menuItem.id === focusIds.USERCENTRICS}
                >
                  <MenuItem
                    menuItem={menuItem}
                    isSettingsMenuItem
                    isSidebarFocused={sidebarFocused}
                    isActive={checkIsActiveMenuItem(menuItem)}
                    onSelected={selectMenuItem}
                  />
                </MenuItemWrapper>
              )
            })}
          </ItemListWrapper>
        </MenuWrapper>
        <AppVersion>{import.meta.env.REACT_APP_VERSION}</AppVersion>
      </SettingsMenu>
    </SettingsSidebarElement>
  )
}

export const SettingsSidebar = memo(SettingsSidebarRaw)
